import { Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { CommonMap } from "../../../_shared";
import { FuelChangesLocations } from '../../../fuel-changes/models/fuel-changes-details.model';
import { IClusterRenderer } from '../../../../_shared/interfaces/maps/base-cluster-renderer';
import { FUEL_CHANGES_DROPDOWN_ITEMS, FUEL_CHANGES_DROPDOWN_LABEL_KEYS } from '../../constants/fuel-changes-dropdown-items.constant';
import { IMarkerFactory } from '../../../../_shared/interfaces/maps/base-marker-factory';
import { FuelChangesMarkerFactory } from '../../helpers/maps/fuel-changes-marker-factory.helper';
import { IMapInfoWindowBuilder } from '../../../../_shared/interfaces/maps/base-map-info-window-builder';
import { FuelChangesMapInfoWindowBuilder } from '../../helpers/maps/fuel-changes-map-info-window-builder.helper';
import { FuelChangesClusterRenderer } from '../../helpers/maps/fuel-changes-cluster-renderer.helper';
import { CommonMapService } from '../../../../_shared/services/map/common-map.service';
import { ActivatedRoute } from '@angular/router';
import { applyMapFilter } from '../../helpers/maps/map-filter.helper';
import { Subject, Subscription } from 'rxjs';
import { AuthService } from '../../../../_shared/services';
import { AccountFilterData, DateIntervalFilterData } from '../../../../_shared/models';
import { FuelChangesMapService } from '../../services/fuel-changes-map.service';
import { AccountDateFilterRequest } from '../../../../_shared/models/accountDateFilter.model';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { PageFilterStateService } from '../../../../_shared/services/page-filter-state.service';
import { CurrencyHelper } from '../../../../_shared/helpers/currency/currency-map.helper';

@Component({
  selector: 'app-fuel-dashboard-map',
  standalone: true,
  imports: [CommonMap],
  templateUrl: './fuel-dashboard-map.component.html',
  styleUrl: './fuel-dashboard-map.component.scss'
})
export class FuelDashboardMapComponent implements OnInit, OnDestroy {
  @Input() mapCenter: google.maps.LatLngLiteral | null = null;
  @Input() isLoading: boolean = false;
  @ViewChild(CommonMap) commonMapComponent!: CommonMap;

  private readonly transloco = inject(TranslocoService);

  dataSubscription: Subscription;
  private dropdownLabelsSubscription: Subscription;
  fuelChangesData: FuelChangesLocations[] = [];
  // Stable filter value (one of dropdownValues) - drives the filter logic and is what CommonMapService stores.
  selectedItem: string = '';
  // Translated label of selectedItem - what the map dropdown shows.
  selectedItemLabel: string = '';
  routeLink: string = '';
  isCollapsed: boolean = false;
  private readonly dropdownValues: string[] = FUEL_CHANGES_DROPDOWN_ITEMS;
  // Translated labels, index-aligned with dropdownValues.
  dropdownItems: string[] = [];
  filteredData: FuelChangesLocations[] = []
  clusterRenderer: IClusterRenderer = new FuelChangesClusterRenderer();
  markerFactory: IMarkerFactory = new FuelChangesMarkerFactory();
  mapInfoWindowBuilder: IMapInfoWindowBuilder = new FuelChangesMapInfoWindowBuilder((key) => this.transloco.translate(key));
  currencyCode: string = '';
  currencySymbol: string = '';
  dateFilter = {} as DateIntervalFilterData;
  accountFilter = {} as AccountFilterData;
  fuelChangesLocations: any[] = [];
  private cancelSubject = new Subject<void>();

  constructor(
    private mapDataService: CommonMapService,
    private activatedRoute: ActivatedRoute,
    private auth: AuthService,
    private pageFilterStateService: PageFilterStateService,
    private fuelChangesMapService: FuelChangesMapService,
    private errorService: ErrorHandlerService
  ) { }

  ngOnInit(): void {
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.setRouteLink();
    const storedItem = this.mapDataService.getSelectedItem();
    this.selectedItem = this.dropdownValues.includes(storedItem) ? storedItem : this.dropdownValues[0];
    this.updateDropdownLabels();
    this.dropdownLabelsSubscription = this.transloco
      .selectTranslate<string[]>(this.dropdownLabelKeys())
      .subscribe((labels) => this.updateDropdownLabels(labels));
    this.loadPageFilterState();

    this.dataSubscription = this.mapDataService.data$.subscribe((data) => {
      if (!this.isCollapsed && data.length === 0) {
        this.loadFuelChangesLocations();
      }

      this.fuelChangesData = data;
      this.applySelection(this.selectedItem);
    });
  }

  private dropdownLabelKeys(): string[] {
    return this.dropdownValues.map(value => FUEL_CHANGES_DROPDOWN_LABEL_KEYS[value] ?? value);
  }

  private updateDropdownLabels(labels: string[] = this.transloco.translate<string[]>(this.dropdownLabelKeys())): void {
    this.dropdownItems = labels;
    this.selectedItemLabel = labels[this.dropdownValues.indexOf(this.selectedItem)] ?? this.selectedItem;
  }

  private async loadFuelChangesLocations(): Promise<void> {
    try {
      if (!this.accountFilter.accountIds || !this.dateFilter.startDate || !this.dateFilter.endDate) {
        return;
      }

      const request: AccountDateFilterRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate,
        timeTo: this.dateFilter.endDate,
        interval: this.dateFilter.interval
      };

      this.cancelSubject.next();
      const response = await this.fuelChangesMapService.getFuelChangesLocations(request, this.cancelSubject);

      if (response.data) {
        this.fuelChangesLocations = response.data?.map(item => {
          return {
            assetId: item.assetId,
            vehicle: item.assetName,
            driver: `${item.firstName} ${item.lastName}`,
            amount: item.amount,
            cost: item.cost,
            type: item.fuelStatusType,
            lat: item.lat,
            lng: item.lng,
            location: item.location,
            timestamp: item.timestamp,
            tank: item.tank,
            fuelConsumption: item.fuelConsumption
          };
        }) ?? [];
        this.mapDataService.updateData(this.fuelChangesLocations);
      }
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private loadPageFilterState(): void {
    const pageFilter = this.pageFilterStateService.getFilter();
    if (pageFilter) {
      this.dateFilter = pageFilter as DateIntervalFilterData;
      this.accountFilter = pageFilter as AccountFilterData;
    }
  }

  applyMapFilter(selectedItem: string, fuelChangesData: FuelChangesLocations[]): void {
    this.filteredData = applyMapFilter(selectedItem, fuelChangesData);
  }

  // The map dropdown emits the translated label; resolve it back to its stable filter value.
  onDropdownChange(selectedLabel: string): void {
    const labelIndex = this.dropdownItems.indexOf(selectedLabel);
    const selectedItem = labelIndex >= 0
      ? this.dropdownValues[labelIndex]
      : (this.dropdownValues.includes(selectedLabel) ? selectedLabel : this.dropdownValues[0]);

    this.applySelection(selectedItem);
  }

  private applySelection(selectedItem: string): void {
    this.selectedItem = selectedItem;
    this.selectedItemLabel = this.dropdownItems[this.dropdownValues.indexOf(selectedItem)] ?? selectedItem;
    this.mapDataService.setSelectedItem(selectedItem);
    this.applyMapFilter(selectedItem, this.fuelChangesData);
  }

  ngOnDestroy(): void {
    this.dropdownLabelsSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
    if (!this.isCollapsed) {
      this.mapDataService.updateData([]);
      this.dataSubscription?.unsubscribe();
    }
  }

  setRouteLink(): void {
    const currentPage = this.activatedRoute.snapshot.routeConfig?.path || '';
    this.isCollapsed = !currentPage.includes('map');
    this.routeLink = this.isCollapsed ? '/fuel-management/fuel/fuel-dashboard-map' : '/fuel-management/fuel';
  }
}